import { useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  DialogActions,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import PLOT_STATUSES from '../plotStatuses';
import type { Plot, EntityId } from '@/types';

interface PlotDeleteDialogProps {
  plot: Plot | null | undefined;
  deletePlot: (plot_id: EntityId) => Promise<void>;
  open: boolean;
  handleClose: () => void;
}

function PlotDeleteDialog({
  plot,
  deletePlot,
  open,
  handleClose,
}: PlotDeleteDialogProps): React.JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const isCompleted = plot?.status === PLOT_STATUSES.COMPLETE;

  const handleSubmit = async () => {
    if (!plot) {
      return;
    }
    setLoading(true);
    await deletePlot(plot.entity_id);
    setLoading(false);
    handleClose();
  };

  return (
    <Dialog onClose={() => handleClose()} open={open}>
      <DialogTitle>
        <Grid container direction='row' alignItems='center'>
          <Grid size={11}>Delete Plot</Grid>
          <Grid size={1} sx={{ maxWidth: '25px' }}>
            <IconButton aria-label='close' onClick={() => handleClose()}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Box
          padding='10px'
          sx={{
            display: 'flex',
            maxWidth: 800,
          }}
        >
          <Alert severity={isCompleted ? 'warning' : 'error'}>
            {isCompleted
              ? 'Completed plots cannot be deleted.'
              : 'Deleting an plot will permanently delete all plot content. This operation is not reversible. Are you sure you want to delete?'}
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color='inherit'
            onClick={handleClose}
            sx={{ mr: 1, flex: '1 1 auto' }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <LoadingButton
            loading={loading}
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={isCompleted}
          >
            Delete
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default PlotDeleteDialog;

